import React, { memo, useEffect, useState, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Logout from '../pages/Logout';
import axios from 'axios';
import { ToastContainer, toast } from "react-toastify";
import { useAuth } from '../../AuthContext';

const navItems = [
    { to: '/candidatedashboard', icon: 'gauge-high', text: 'Dashboard' },
    { to: '/myprofile', icon: 'user-tie', text: 'My Profile' },
    // { to: '/candidate-resume', icon: 'file-lines', text: 'My Resume' },
    { to: '/candidate-applied-jobs', icon: 'briefcase', text: 'Applied Jobs' },
    // { to: '/candidate-sortlisted-jobs', icon: 'layer-group', text: 'Shortlisted Jobs' },
    { to: '/candidate-cv-manager', icon: 'file-zipper', text: 'CV Manager' },
    { to: '/saved-jobs', icon: 'bookmark', text: 'Saved Jobs' },
    // { to: '/candidate-job-alerts', icon: 'bell', text: 'Job Alerts' },
    { to: '/settings', icon: 'cog', text: 'Settings' },
    { to: '/deleteaccount', icon: 'trash-can', text: 'Delete Profile' },
];

const NavItem = memo(({ to, icon, children, isActive }) => (
    <li>
        <Link className={isActive ? 'active' : ''} to={to}>
            <i className={`far fa-${icon}`} /> {children}
        </Link>
    </li>
));

const ProfileImage = memo(({ imageUrl, fileInputRef, onFileChange }) => (
    <div className="text-center mb-3">
        <div className="position-relative d-inline-block">
            <img
            loading="lazy"
            role="presentation"
            decoding="async"
            fetchpriority="high"            
                src={imageUrl ? `${process.env.REACT_APP_API_URL}${imageUrl}` : "assets/img/candidate/default.jpg"}
                alt="Profile"
                className="rounded-circle border border-secondary"
                style={{ width: "120px", height: "120px", objectFit: "cover" }}
                onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "assets/img/candidate/default.jpg";
                }}
            />
            <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                className="d-none"
                onChange={onFileChange}
            />
            <button
                type="button"
                className="btn btn-sm btn-primary position-absolute bottom-0 end-0 rounded-circle"
                style={{ transform: "translate(25%, 25%)" }}
                onClick={() => fileInputRef.current.click()}
            >
                <i className="far fa-camera" />
            </button>
        </div>
    </div>
));

const CandidateDashboardNav = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { auth } = useAuth();
    const [userDetails, setDetails] = useState([]);
    const fileInputRef = useRef(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const userid = localStorage.getItem('userid');

    // Authentication check on component mount
    useEffect(() => {
        if (!auth.isAuthenticated || auth.role !== 'candidate') {
            navigate('/login');
        }
    }, [auth.isAuthenticated, auth.role, navigate]);

    // Fetch user data only if authenticated
    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const userResponse = await axios.get(
                    `${process.env.REACT_APP_API_URL}users/details/${userid}`
                );
                setDetails(userResponse.data);
            } catch (error) {
                console.error("Error fetching user data:", error);
            }
        };

        if (auth.isAuthenticated && auth.role === 'candidate') {
            fetchUserData();
        }
    }, [auth.isAuthenticated, auth.role, userid]);

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('profilePicture', file);

        try {
            await axios.put(
                `${process.env.REACT_APP_API_URL}upload/upload-profile/${userid}`,
                formData,
                {
                    headers: { 'Content-Type': 'multipart/form-data' },
                }
            );
            toast.success("Profile uploaded successfully");
        } catch (error) {
            toast.error("Unable to upload profile! Please try again");
            console.error("Error uploading profile picture:", error.response?.data || error.message);
        }
    };

    const confirmLogout = () => {
        setIsModalOpen(false);
        localStorage.clear();
        navigate('/login');
    };

    const cancelLogout = () => {
        setIsModalOpen(false);
    };

    return (
        <div className="col-lg-3">
            <ToastContainer autoClose={2000} />
            <div className="user-profile-sidebar">
                <div className="user-profile-sidebar-top">
                    <ProfileImage
                        imageUrl={userDetails.profile_url}
                        fileInputRef={fileInputRef}
                        onFileChange={handleFileChange}
                    />
                    <h4>{userDetails.name}</h4>
                    <p>{userDetails.designation}</p>
                </div>
                <ul className="user-profile-sidebar-list">
                    {navItems.map(({ to, icon, text }) => (
                        <NavItem
                            key={to}
                            to={to}
                            icon={icon}
                            isActive={location.pathname === to}
                        >
                            {text}
                        </NavItem>
                    ))}
                </ul>
            </div>
            <Logout
                isOpen={isModalOpen}
                onConfirm={confirmLogout}
                onCancel={cancelLogout}
            />
        </div>
    );
};

export default React.memo(CandidateDashboardNav);